import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface APInvoiceDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave?: (updatedData: any, changes?: any[]) => void
}

const APInvoiceDialog: React.FC<APInvoiceDialogProps> = ({ data, shipmentId, documentId, onSave }) => {
  // Define sections for AP Invoice
  const sections: DocumentSection[] = [
    {
      id: 'invoiceInfo',
      title: 'Invoice Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Invoice #', path: 'invoiceNumber' },
        { type: 'field', label: 'Purchase Order #', path: 'purchaseOrderNumber' },
        { type: 'field', label: 'Reference #', path: 'referenceNumber' },
        { type: 'field', label: 'Invoice Date', path: 'invoiceDate' },
        { type: 'field', label: 'Due Date', path: 'dueDate' },
        { type: 'field', label: 'Currency', path: 'currency' },
      ],
    },
    {
      id: 'parties',
      title: 'Parties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Vendor',
          path: 'vendor',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Contact', path: 'contact' },
            { label: 'Vendor Number', path: 'vendorNumber' },
            { label: 'Payment Terms', path: 'paymentTerms' },
          ],
        },
        {
          type: 'object',
          label: 'Buyer',
          path: 'buyer',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Tax ID', path: 'taxId' },
            { label: 'Contact', path: 'contact' },
            { label: 'Buyer Number', path: 'buyerNumber' },
          ],
        },
      ],
    },
    {
      id: 'bankDetails',
      title: 'Bank Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Bank Details',
          path: 'vendor.bankDetails',
          fields: [
            { label: 'Bank Name', path: 'bankName' },
            { label: 'Account Number', path: 'accountNumber' },
            { label: 'Routing Number', path: 'routingNumber' },
            { label: 'SWIFT Code', path: 'swiftCode' },
            { label: 'IBAN', path: 'iban' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'lineItems',
      title: 'Line Items',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Line Items',
          path: 'lineItems',
          columns: [
            { label: 'Item Number', path: 'itemNumber' },
            { label: 'Description', path: 'description' },
            { label: 'Quantity', path: 'quantity' },
            { label: 'Unit Price', path: 'unitPrice' },
            { label: 'Unit of Measure', path: 'unitOfMeasure' },
            { label: 'Amount', path: 'amount' },
            { label: 'Tax Rate', path: 'taxRate' },
            { label: 'Tax Amount', path: 'taxAmount' },
            { label: 'GL Account', path: 'glAccount' },
            { label: 'Cost Center', path: 'costCenter' },
            { label: 'PO Line Number', path: 'poLineNumber' },
            { label: 'Receipt Number', path: 'receiptNumber' },
          ],
        },
      ],
    },
    {
      id: 'totals',
      title: 'Totals',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Subtotal', path: 'subtotal' },
        { type: 'field', label: 'Tax Total', path: 'taxTotal' },
        { type: 'field', label: 'Discount Amount', path: 'discountAmount' },
        { type: 'field', label: 'Shipping Amount', path: 'shippingAmount' },
        { type: 'field', label: 'Total Amount', path: 'totalAmount' },
        { type: 'field', label: 'Amount Due', path: 'amountDue' },
      ],
    },
    {
      id: 'otherCharges',
      title: 'Other Charges',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Other Charges',
          path: 'otherCharges',
          columns: [
            { label: 'Description', path: 'description' },
            { label: 'Amount', path: 'amount' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'paymentInfo',
      title: 'Payment Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Payment Status', path: 'paymentStatus' },
        { type: 'field', label: 'Payment Method', path: 'paymentMethod' },
        { type: 'field', label: 'Payment Due Date', path: 'paymentDueDate' },
      ],
    },
    {
      id: 'earlyPaymentDiscount',
      title: 'Early Payment Discount',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Early Payment Discount',
          path: 'earlyPaymentDiscount',
          fields: [
            { label: 'Percentage', path: 'percentage' },
            { label: 'Days Valid', path: 'daysValid' },
            { label: 'Amount', path: 'amount' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'approvalInfo',
      title: 'Approval Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Approval Status', path: 'approvalStatus' },
        { type: 'field', label: 'Approved By', path: 'approvedBy' },
        { type: 'field', label: 'Approval Date', path: 'approvalDate' },
      ],
    },
    {
      id: 'shipmentDetails',
      title: 'Shipment Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Shipment Details',
          path: 'shipmentDetails',
          fields: [
            { label: 'Ship Date', path: 'shipDate' },
            { label: 'Delivery Date', path: 'deliveryDate' },
            { label: 'Ship Method', path: 'shipMethod' },
            { label: 'Tracking Number', path: 'trackingNumber' },
            { label: 'Incoterms', path: 'incoterms' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'taxDetails',
      title: 'Tax Details',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Tax Details',
          path: 'taxDetails',
          fields: [
            { label: 'Tax Registration Number', path: 'taxRegistrationNumber' },
            { label: 'Tax Type', path: 'taxType' },
            { label: 'Tax Exemption Number', path: 'taxExemptionNumber' },
            { label: 'Tax Exemption Reason', path: 'taxExemptionReason' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'matchingInfo',
      title: 'Matching Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Match Status', path: 'matchStatus' },
        { type: 'field', label: 'Match Type', path: 'matchType' },
      ],
    },
    {
      id: 'exceptions',
      title: 'Exceptions',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Exceptions',
          path: 'exceptions',
          columns: [
            { label: 'Type', path: 'type' },
            { label: 'Description', path: 'description' },
            { label: 'Amount', path: 'amount' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'attachments',
      title: 'Attachments',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Attachments',
          path: 'attachments',
          columns: [
            { label: 'Name', path: 'name' },
            { label: 'Type', path: 'type' },
            { label: 'Reference', path: 'reference' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'additionalInfo',
      title: 'Additional Information',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Notes', path: 'notes', fullWidth: true },
        { type: 'field', label: 'Terms', path: 'terms', fullWidth: true },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="AP Invoice"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default APInvoiceDialog
